import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { APP_CONFIG_DEFAULTS, mergeConfig } from './useAppConfig.defaults'
import type { AppConfigData } from './useAppConfig.types'

export { APP_CONFIG_DEFAULTS } from './useAppConfig.defaults'
export type { AppConfigData } from './useAppConfig.types'

// ─── React Query hooks ──────────────────────────

export function useAppConfig() {
  return useQuery({
    queryKey: ['app-config'],
    queryFn: async (): Promise<AppConfigData> => {
      const res = await fetch('/api/app-config')
      if (!res.ok) return APP_CONFIG_DEFAULTS
      const data = await res.json()
      return mergeConfig(data)
    },
    staleTime: 5 * 60 * 1000, // cache 5 menit
  })
}

export function useUpdateAppConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (config: Partial<AppConfigData>) => {
      const res = await fetch('/api/admin/config', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (!res.ok) throw new Error('Gagal menyimpan pengaturan')
      return res.json()
    },
    onSuccess: (data) => {
      qc.setQueryData(['app-config'], mergeConfig(data))
    },
  })
}

// ─── Context (opsional, untuk akses tanpa refetch) ──

const AppConfigContext = createContext<AppConfigData>(APP_CONFIG_DEFAULTS)

export function AppConfigProvider({ children, config }: { children: React.ReactNode; config: AppConfigData }) {
  return <AppConfigContext.Provider value={config}>{children}</AppConfigContext.Provider>
}

export function useAppConfigCtx() {
  return useContext(AppConfigContext)
}
